import { useLocation } from "react-router-dom";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { useCanUseRevenueRecognition } from "@hooks/useCanUseRevenueRecognition";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { getAvailableScheduleHubScopes } from "@components/accounting/schedules/scheduleHubScope";
import {
    CalendarIcon,
    ChartOfAccountsIcon,
    ClassIcon,
    DirectoryIcon,
    IntercompanyIcon,
    ReconciledIcon,
} from "@icons";
import { isChartOfAccountsRoute } from "../accountingNavigation";
import { INTERCOMPANY_PATH } from "./workspaceSideNavSections";
import { useNavigationShortcutKeys } from "./useNavigationShortcutKeys";
import type { SideNavSectionState } from "./sideNavSection.types";

/** Today's Accounting group, promoted to a section; business entities only. */
export const AccountingSection = ({
    section,
    canUseConsolidation,
}: {
    section: SideNavSectionState;
    canUseConsolidation: boolean;
}) => {
    const { pathname } = useLocation();
    const canUseRevenueRecognition = useCanUseRevenueRecognition();
    const { schedulesExpenses: isExpensesAvailable } = useTypedFlags();
    const scheduleHubScopes = getAvailableScheduleHubScopes({
        isExpensesAvailable,
    });
    const shortcutKeys = useNavigationShortcutKeys();

    return (
        <SideNavSection {...section}>
            <SideNavItem
                to="/chart-of-accounts"
                isActive={isChartOfAccountsRoute(pathname)}
                icon={<ChartOfAccountsIcon />}
                label="Chart of Accounts"
                shortcut={shortcutKeys("/chart-of-accounts")}
            />
            <SideNavItem
                to="/counterparties"
                end
                icon={<DirectoryIcon />}
                label="Counterparties"
                shortcut={shortcutKeys("/counterparties")}
            />
            <SideNavItem
                to="/classes"
                end
                icon={<ClassIcon />}
                label="Classes"
                shortcut={shortcutKeys("/classes")}
            />
            <SideNavItem
                to="/reconciliation"
                icon={<ReconciledIcon />}
                label="Reconciliation"
                shortcut={shortcutKeys("/reconciliation")}
            />
            {canUseRevenueRecognition &&
                scheduleHubScopes.map((scope) => (
                    <SideNavItem
                        key={scope.path}
                        to={scope.path}
                        end
                        icon={<CalendarIcon />}
                        label={scope.label}
                    />
                ))}
            {canUseConsolidation && (
                <SideNavItem
                    to={INTERCOMPANY_PATH}
                    end
                    icon={<IntercompanyIcon />}
                    label="Intercompany"
                />
            )}
        </SideNavSection>
    );
};
